import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseEntityResource } from '../../core/abstractions/base-entity.resource';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { GridStoredConfig } from '../../models/core/grid-stored.config';
import { ResourceConfig } from '../../models/core/resource.config';
import { DashboardConfigurationModel } from '../../models/dashboard-configuration.model';
import { LayoutSettingsModel } from '../../models/layout-settings.model';
import { PermissionModel } from '../../models/permission.model';
import { RoleModel } from '../../models/role.model';
import { OptionalType } from '../../models/types/optional.type';

@Injectable({
    providedIn: 'root'
})
export class RolesResource extends BaseEntityResource<RoleModel> {
    constructor() {
        super(new ResourceConfig({ baseController: 'roles' }));
    }

    /**
     * Returns all roles
     */
    getAll(): Observable<ApiResponseModel<RoleModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<RoleModel[]>>(this.getUrl(''));
    }

    /**
     * Returns all permissions for the current user.
     */
    getCurrentPermissions(): Observable<ApiResponseModel<PermissionModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<PermissionModel[]>>(this.getUrl('current/permissions'));
    }

    /**
     * Returns all permissions for a specific role.
     */
    getPermissions(roleId: string): Observable<ApiResponseModel<PermissionModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<PermissionModel[]>>(this.getUrl(`${roleId}/permissions`));
    }

    /**
     * Updates the permissions for the specified role.
     */
    editPermissions(roleId: string, newPermissions: PermissionModel[]): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePut<ApiResponseModel<boolean>>(this.getUrl(`${roleId}/permissions`), newPermissions);
    }

    /**
     * Saves the layout settings for the specified role.
     */
    saveLayoutSettings(data: LayoutSettingsModel, roleId: number, layoutKey = 'layout'): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl(`${roleId}/settings/${layoutKey}`), data);
    }

    getLayoutSettings(roleId: number, layoutKey = 'layout'): Observable<LayoutSettingsModel> {
        return this._requestService.makeGet<ApiResponseModel<string>>(this.getUrl(`${roleId}/settings/${layoutKey}`)).pipe(
            map(r => {
                if (!!r.value) {
                    return JSON.parse(r.value) as LayoutSettingsModel;
                } else {
                    const emptyDefaultModel = new LayoutSettingsModel();
                    emptyDefaultModel.dashboard = new DashboardConfigurationModel();
                    return emptyDefaultModel;
                }
            })
        );
    }

    /**
     * Saves the grid default settings for the specified role.
     */
    saveGridDefaultSettings(data: GridStoredConfig, roleId: number, gridName: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl(`${roleId}/settings/grid_${gridName}`), data);
    }

    getGridDefaultSettings(roleId: number, gridName: string): Observable<OptionalType<GridStoredConfig>> {
        return this._requestService
            .makeGet<ApiResponseModel<string>>(this.getUrl(`${roleId}/settings/grid_${gridName}`))
            .pipe(map(r => (r.value ? (JSON.parse(r.value) as GridStoredConfig) : undefined)));
    }
}
